// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Waterfall chart component for asset transition visualization.
 *
 * Shows step-by-step breakdown of asset equation using visx components.
 * Displays: Starting + Added = Total - Sales - Deployments = Ending
 */
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import {
  FixedAssetsRollforwardData,
  InventoryRollforwardData,
} from '@/report/api/types';
import { AssetWaterfallChartSkeleton } from '@/report/components/asset-waterfall-chart-skeleton';
import {
  ASSET_TRANSITION_PHASES,
  AssetTransitionPhase,
} from '@/report/constants/asset-transition-colors';
import { RollforwardWaterfallDisplayMetricType } from '@/report/types';
import { formatCurrency } from '@/utilities/number';
import { Box, Tooltip, useTheme } from '@mui/material';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { Text } from '@visx/text';
import { Decimal } from 'decimal.js';
import { useRef } from 'react';

interface Props {
  data?: InventoryRollforwardData | FixedAssetsRollforwardData;
  metricType: RollforwardWaterfallDisplayMetricType;
  currencyCode?: IsoCurrencyCode;
  isLoading?: boolean;
}

interface WaterfallDataPoint {
  phase: AssetTransitionPhase;
  value: number;
  amount: Decimal;
  type: 'absolute' | 'relative' | 'total';
  cumulative: number;
}

export function AssetWaterfallChart(props: Props) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);

  const getValue = (item: { units: number; dollars: string | number }) => {
    if (props.metricType === 'units') {
      return new CurrencyDecimal(item.units);
    }

    return new CurrencyDecimal(item.dollars);
  };

  const formatValue = (
    value: number,
    options?: { abbreviate?: boolean; digits?: number }
  ) => {
    // Abbreviated labels and axis ticks compact the amount; tooltips show it in full.
    if (props.metricType === 'dollars') {
      return formatCurrency(Math.abs(value), {
        abbreviate: options?.abbreviate ?? false,
        digits: options?.digits ?? 1,
        currencyCode: props.currencyCode,
      });
    }

    return Math.abs(value).toLocaleString();
  };

  const formatTooltipAmount = (amount: Decimal) =>
    props.currencyCode === undefined
      ? formatCurrencyValue(null)
      : formatAmountWithCurrency(amount.abs(), props.currencyCode);

  const margin = { top: 20, right: 20, bottom: 40, left: 80 };
  const width = Math.max(containerSize.width - margin.left - margin.right, 600);
  const height = 400;
  // Minimum visible height for zero-value bars to ensure they remain visible
  // even when positioned above the baseline
  const minBarHeight = 2;

  // Show skeleton loader while container size is being calculated, data is loading, or data is unavailable
  if (containerSize.width === 0 || props.isLoading || !props.data) {
    return (
      <Box
        ref={containerRef}
        width="100%"
        height={height + margin.top + margin.bottom}
      >
        <AssetWaterfallChartSkeleton height={height} />
      </Box>
    );
  }

  const isFixedAssetsData = 'depreciated' in props.data;

  const startValue = getValue(props.data.start);
  const addedValue = getValue(props.data.added);
  const outflowValue = getValue(
    isFixedAssetsData
      ? (props.data as FixedAssetsRollforwardData).depreciated
      : (props.data as InventoryRollforwardData).deployed
  );
  // A fixed-asset unit waterfall does not step down for depreciation
  // before the sold and scrapped bars.
  const outflowStepValue =
    isFixedAssetsData && props.metricType === 'units'
      ? new CurrencyDecimal(0)
      : outflowValue;
  const soldValue = getValue(props.data.sold);
  const totalAvailableValue = startValue.plus(addedValue);

  const waterfallData: WaterfallDataPoint[] = [
    {
      phase: 'start',
      value: startValue.toNumber(),
      amount: startValue,
      type: 'absolute',
      cumulative: 0,
    },
    {
      phase: 'added',
      value: addedValue.toNumber(),
      amount: addedValue,
      type: 'relative',
      cumulative: startValue.toNumber(),
    },
    {
      phase: 'totalAvailable',
      value: totalAvailableValue.toNumber(),
      amount: totalAvailableValue,
      type: 'total',
      cumulative: 0,
    },
    {
      phase: isFixedAssetsData ? 'depreciated' : 'deployed',
      value: outflowValue.negated().toNumber(),
      amount: outflowValue.negated(),
      type: 'relative',
      cumulative: totalAvailableValue.toNumber(),
    },
    {
      phase: 'sold',
      value: soldValue.negated().toNumber(),
      amount: soldValue.negated(),
      type: 'relative',
      cumulative: totalAvailableValue.minus(outflowStepValue).toNumber(),
    },
    {
      phase: 'scrapped',
      value: getValue(props.data.scrapped).negated().toNumber(),
      amount: getValue(props.data.scrapped).negated(),
      type: 'relative',
      cumulative: totalAvailableValue
        .minus(soldValue)
        .minus(outflowStepValue)
        .toNumber(),
    },
    {
      phase: 'end',
      value: getValue(props.data.end).toNumber(),
      amount: getValue(props.data.end),
      type: 'total',
      cumulative: 0,
    },
  ];

  const xScale = scaleBand({
    domain: waterfallData.map((d) => d.phase),
    range: [0, width],
    padding: 0.2,
  });

  const maxValue = Math.max(
    totalAvailableValue.toNumber(),
    getValue(props.data.end).toNumber()
  );
  const yScale = scaleLinear({
    domain: [0, maxValue * 1.1],
    range: [height, 0],
  });

  const getBarHeight = (dataPoint: WaterfallDataPoint) => {
    let height: number;

    if (dataPoint.type === 'absolute' || dataPoint.type === 'total') {
      // Absolute/Total bars: Height from baseline (0) to value
      // Used for: starting inventory, ending inventory, total available
      height = yScale(0) - yScale(Math.abs(dataPoint.value));
    } else {
      // Relative bars: Height represents the change/transition amount
      // Used for: added, sold, deployed (the +/- transitions)
      // Height = difference between cumulative start and cumulative end positions
      height = Math.abs(
        yScale(dataPoint.cumulative + dataPoint.value) -
          yScale(dataPoint.cumulative)
      );
    }

    // Ensure minimum height for visibility, especially for zero values
    // This prevents bars from being invisible when positioned above baseline
    return Math.max(height, minBarHeight);
  };

  const getBarY = (dataPoint: WaterfallDataPoint) => {
    if (dataPoint.type === 'absolute' || dataPoint.type === 'total') {
      // Absolute/Total bars: Position from baseline (0)
      // These bars always start from the bottom of the chart
      const baseY = yScale(Math.abs(dataPoint.value));

      // Special case: Zero values need visible minimum height
      if (dataPoint.value === 0) {
        return baseY - minBarHeight;
      }
      return baseY;
    }

    // Relative bars: Position based on cumulative waterfall flow
    if (dataPoint.value > 0) {
      // Positive transitions (e.g., "added"): Bar extends upward from cumulative base
      // Y position is at the top of the bar (cumulative + value)
      return yScale(dataPoint.cumulative + dataPoint.value);
    }
    if (dataPoint.value < 0) {
      // Negative transitions (e.g., "sold", "deployed"): Bar extends downward
      // Y position is at the cumulative level (before the reduction)
      return yScale(dataPoint.cumulative);
    }

    // Zero value in relative bar: Show minimum height at cumulative level
    return yScale(dataPoint.cumulative) - minBarHeight;
  };

  return (
    <Box
      ref={containerRef}
      width="100%"
      height={height + margin.top + margin.bottom}
      sx={{ position: 'relative' }}
    >
      <svg
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          <AxisLeft
            scale={yScale}
            tickFormat={(value) => {
              const numValue = Number(value);
              // Special handling for zero - no abbreviation or decimals, but still the
              // selected currency's symbol rather than a hardcoded dollar.
              if (numValue === 0 && props.metricType === 'dollars') {
                return formatValue(0, { digits: 0 });
              }
              return formatValue(numValue, {
                abbreviate: props.metricType === 'dollars',
                digits: props.metricType === 'dollars' ? 1 : undefined,
              });
            }}
            stroke={theme.palette.divider}
            tickStroke={theme.palette.text.secondary}
            tickLabelProps={() => ({
              fill: theme.palette.text.secondary,
              fontSize: 12,
              textAnchor: 'end',
              dy: '0.33em',
            })}
          />
          <AxisBottom
            top={height}
            scale={xScale}
            stroke={theme.palette.divider}
            tickStroke={theme.palette.text.secondary}
            tickFormat={(phase) => ASSET_TRANSITION_PHASES[phase].displayName}
            tickLabelProps={() => ({
              fill: theme.palette.text.secondary,
              fontSize: 12,
              textAnchor: 'middle',
              dy: '0.33em',
            })}
          />
          {waterfallData.map((dataPoint) => {
            const barX = xScale(dataPoint.phase) || 0;
            const barY = getBarY(dataPoint);
            const barHeight = getBarHeight(dataPoint);
            const barWidth = xScale.bandwidth();

            const phaseConfig = ASSET_TRANSITION_PHASES[dataPoint.phase];

            return (
              <g key={dataPoint.phase}>
                <Bar
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={
                    dataPoint.phase === 'depreciated' &&
                    props.metricType === 'units'
                      ? '#eeeeee'
                      : phaseConfig.color(theme)
                  }
                />
                <Text
                  x={barX + barWidth / 2}
                  y={barY - 5}
                  textAnchor="middle"
                  fontSize={16}
                  fontWeight="bold"
                  fill={theme.palette.text.primary}
                >
                  {formatValue(dataPoint.value, {
                    abbreviate: props.metricType === 'dollars',
                  })}
                </Text>
                {/* Connecting lines for specific transitions */}
                {(dataPoint.phase === 'start' ||
                  dataPoint.phase === 'added' ||
                  dataPoint.phase === 'totalAvailable' ||
                  dataPoint.phase === 'deployed' ||
                  dataPoint.phase === 'depreciated' ||
                  dataPoint.phase === 'sold') &&
                  (() => {
                    let targetPhase: AssetTransitionPhase | null = null;

                    // Define the connections based on data type
                    if (dataPoint.phase === 'start') {
                      targetPhase = 'added';
                    } else if (dataPoint.phase === 'added') {
                      targetPhase = 'totalAvailable';
                    } else if (dataPoint.phase === 'totalAvailable') {
                      targetPhase = isFixedAssetsData
                        ? 'depreciated'
                        : 'deployed';
                    } else if (dataPoint.phase === 'sold') {
                      targetPhase = 'scrapped';
                    } else if (dataPoint.phase === 'deployed') {
                      targetPhase = 'sold';
                    } else if (dataPoint.phase === 'depreciated') {
                      targetPhase = 'sold';
                    }

                    if (!targetPhase) return null;

                    const targetBarX = xScale(targetPhase) || 0;
                    const targetDataPoint = waterfallData.find(
                      (d) => d.phase === targetPhase
                    );

                    if (!targetDataPoint) return null;

                    // Calculate y-coordinates based on specific phase connections
                    let y1, y2;

                    if (
                      dataPoint.phase === 'start' &&
                      targetPhase === 'added'
                    ) {
                      // Starting top to Added bottom
                      y1 = yScale(dataPoint.value);
                      y2 = yScale(targetDataPoint.cumulative);
                    } else if (
                      dataPoint.phase === 'added' &&
                      targetPhase === 'totalAvailable'
                    ) {
                      // Added top to TotalAvailable top
                      y1 = yScale(dataPoint.cumulative + dataPoint.value);
                      y2 = yScale(targetDataPoint.value);
                    } else if (
                      dataPoint.phase === 'totalAvailable' &&
                      (targetPhase === 'deployed' ||
                        targetPhase === 'depreciated')
                    ) {
                      // TotalAvailable top to Depreciated top
                      y1 = yScale(targetDataPoint.cumulative);
                      y2 = yScale(targetDataPoint.cumulative);
                    } else if (
                      dataPoint.phase === 'sold' &&
                      targetPhase === 'scrapped'
                    ) {
                      // Depreciated bottom to next phase top
                      y1 = yScale(targetDataPoint.cumulative);
                      y2 = yScale(targetDataPoint.cumulative);
                    } else if (
                      (dataPoint.phase === 'deployed' ||
                        dataPoint.phase === 'depreciated') &&
                      targetPhase === 'sold'
                    ) {
                      // Previous phase bottom to Scrapped top
                      y1 = yScale(targetDataPoint.cumulative);
                      y2 = yScale(targetDataPoint.cumulative);
                    } else {
                      // Default logic
                      y1 = yScale(dataPoint.cumulative);
                      y2 = yScale(targetDataPoint.cumulative);
                    }

                    return (
                      <line
                        key={`line-${dataPoint.phase}-${targetPhase}`}
                        x1={barX + barWidth}
                        y1={y1}
                        x2={targetBarX}
                        y2={y2}
                        stroke={theme.palette.grey[400]}
                        strokeWidth={2}
                        strokeDasharray="2,3"
                      />
                    );
                  })()}
                {/* Special connection from scrapped to ending */}
                {dataPoint.phase === 'scrapped' &&
                  (() => {
                    const endingBarX = xScale('end') || 0;
                    const endingDataPoint = waterfallData.find(
                      (d) => d.phase === 'end'
                    );

                    if (!endingDataPoint) return null;

                    return (
                      <line
                        key={`line-scrapped-ending`}
                        x1={barX + barWidth}
                        y1={yScale(dataPoint.cumulative + dataPoint.value)}
                        x2={endingBarX}
                        y2={yScale(endingDataPoint.value)}
                        stroke={theme.palette.grey[400]}
                        strokeWidth={2}
                        strokeDasharray="2,3"
                      />
                    );
                  })()}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Tooltip overlays for dollar values */}
      {waterfallData.map((dataPoint) => {
        if (props.metricType === 'units' && dataPoint.phase !== 'depreciated') {
          return null;
        }

        const barX = xScale(dataPoint.phase) || 0;
        const barY = getBarY(dataPoint);
        const barHeight = getBarHeight(dataPoint);
        const barWidth = xScale.bandwidth();

        return (
          <Tooltip
            key={`tooltip-${dataPoint.phase}`}
            title={
              dataPoint.phase === 'depreciated' && props.metricType === 'units'
                ? 'Depreciated assets do not actually leave fixed assets, but the count of ' +
                  formatValue(dataPoint.value) +
                  ' is provided for reference'
                : formatTooltipAmount(dataPoint.amount)
            }
            placement="top"
            arrow
          >
            <Box
              sx={{
                position: 'absolute',
                left: margin.left + barX,
                top: margin.top + barY,
                width: barWidth,
                height: barHeight,
                cursor: 'pointer',
                zIndex: 1,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}
